// admin/users/UserDetailDrawer.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { 
  MapPin, 
  Calendar, 
  Mail, 
  Shield, 
  Award,
  Bike,
  Activity,
  User,
  AlertTriangle,
  CreditCard,
  Trophy,
  Info
} from "lucide-react";
import { toast } from "react-toastify";
import { suspendUser, unsuspendUser } from "@/store/slices/adminSlice";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import TextArea from "../../common/TextArea";
import TextInput from "../../common/TextInput";
import Badge from "../../common/Badge";
import EntityAuditHistory from "../audit/EntityAuditHistory";

const UserDetailDrawer = ({ user, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("details");
  
  // Suspend Modal State
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendDuration, setSuspendDuration] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suspendError, setSuspendError] = useState("");

  // Calculate total distance
  const totalDistance =
    (user.koset_distance || 0) + (user.activity_distance || 0);

  const handleSuspend = async () => {
    if (!suspendReason || suspendReason.length < 10) {
      setSuspendError("Reason must be at least 10 characters.");
      return;
    }
    
    setIsSubmitting(true);
    setSuspendError("");

    try {
      await dispatch(suspendUser({
        userId: user._id,
        reason: suspendReason,
        duration_days: suspendDuration ? parseInt(suspendDuration) : undefined
      })).unwrap();
      
      toast.success("User suspended successfully");
      setSuspendModalOpen(false);
      setSuspendReason("");
      setSuspendDuration("");
    } catch (error) {
      toast.error(error.message || "Failed to suspend user");
      setSuspendError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!window.confirm("Are you sure you want to unsuspend this user?")) return;
    
    setIsSubmitting(true);
    try {
      await dispatch(unsuspendUser(user._id)).unwrap();
      toast.success("User unsuspended successfully");
    } catch (error) {
      toast.error(error.message || "Failed to unsuspend user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleTone = (role) => (role === "admin" ? "admin" : "muted");
  
  const getStatusTone = (status) => {
    switch(status) {
      case "active": return "success";
      case "suspended": return "warning";
      case "deleted": return "danger";
      default: return "muted";
    }
  };

  const InfoRow = ({ icon: Icon, label, value, className = "" }) => (
    <div className={`flex items-start gap-3 ${className}`}>
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
      <div>
        <div className="text-xs text-gray-500 font-medium">{label}</div>
        <div className="text-sm text-gray-800 dark:text-gray-200 font-medium break-all">
          {value || <span className="text-gray-400 italic">N/A</span>}
        </div>
      </div>
    </div>
  );

  const SectionTitle = ({ icon: Icon, title }) => (
    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
      {Icon && <Icon className="w-3 h-3" />} {title}
    </h4>
  );

  return (
    <>
      <Modal 
        open={true} 
        onClose={onClose} 
        title="User Details" 
        maxWidth="max-w-4xl"
      >
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "details"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "history"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
            onClick={() => setActiveTab("history")}
          >
            Governance History
          </button>
          {(user.role === "admin" || user.role === "moderator") && (
            <button
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === "activity_trail"
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("activity_trail")}
            >
              Activity Trail
            </button>
          )}
        </div>

        {activeTab === "details" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-2">
          
          {/* LEFT COLUMN: Identity & Key Status */}
          <div className="md:col-span-1 space-y-6">
            
            {/* 1. Primary Identity */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 text-center border border-gray-100 dark:border-gray-700">
              <div className="w-24 h-24 mx-auto rounded-full bg-white dark:bg-gray-700 mb-4 overflow-hidden border-4 border-white dark:border-gray-600 shadow-md">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <User className="w-10 h-10" />
                  </div>
                )}
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{user.name}</h2>
              <div className="text-sm text-gray-500 mb-3">@{user.username}</div>
              
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <Badge tone={getRoleTone(user.role)} className="uppercase text-[10px]">
                  {user.role}
                </Badge>
                {user.is_member && (
                  <Badge tone="info" icon={<Award className="w-3 h-3" />}>Member</Badge>
                )}
              </div>

              <div className="text-left w-full space-y-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <InfoRow icon={Mail} label="Email" value={user.email} />
              </div>
            </div>

            {/* 2. Account Status & Governance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
               <SectionTitle icon={Shield} title="Account Status" />
               <div className="space-y-4">
                  <InfoRow 
                    label="Current Status" 
                    value={
                      <Badge tone={getStatusTone(user.status)}>
                        {user.status || "Active"}
                      </Badge>
                    } 
                  />
                  <InfoRow 
                    label="Joined Platform" 
                    icon={Calendar}
                    value={user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"} 
                  />

                  {user.status === 'suspended' && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30 text-sm">
                      <div className="font-bold text-red-800 dark:text-red-400 flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4" /> Suspended
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-2">"{user.suspension.reason}"</p>
                      {user.suspension.suspended_until && (
                        <div className="text-xs text-gray-500">
                          Until: {new Date(user.suspension.suspended_until).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  )}
               </div>
            </div>

          </div>

          {/* RIGHT COLUMN: Details & Stats */}
          <div className="md:col-span-2 space-y-6">
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
               {/* 3. Membership & Subscription */}
               <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <SectionTitle icon={CreditCard} title="Membership" />
                  <div className="space-y-3">
                     <InfoRow 
                        label="Plan Type" 
                        value={user.membership?.plan ? <Badge tone="info">{user.membership.plan}</Badge> : "Free"} 
                     />
                     <InfoRow 
                        label="Expiry Date" 
                        icon={Calendar} 
                        value={user.membership?.expiry_date ? new Date(user.membership.expiry_date).toLocaleDateString() : "N/A"} 
                     />
                     <InfoRow 
                        label="Member Status" 
                        value={user.is_member ? "Active" : "Inactive"} 
                     />
                  </div>
               </div>

               {/* 5. Demographics */}
               <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <SectionTitle icon={User} title="Demographics" />
                  <div className="space-y-3">
                     <InfoRow icon={MapPin} label="Location" value={user.location} />
                     <div className="grid grid-cols-2 gap-2">
                       <InfoRow label="Gender" value={user.gender} />
                       <InfoRow label="DOB" value={user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : null} /> 
                       {/* Assuming date_of_birth is string or date object */}
                     </div>
                     <InfoRow 
                       icon={Info} 
                       label="Bio" 
                       value={user.bio} 
                       className="col-span-2"
                     />
                  </div>
               </div>
            </div>

            {/* 4. Engagement & Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
               <SectionTitle icon={Activity} title="Engagement & Stats" />
               <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                     <div className="text-gray-500 text-xs mb-1">Total Rides</div>
                     <div className="text-xl font-bold text-gray-900 dark:text-white">{user.total_rides || 0}</div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                     <div className="text-gray-500 text-xs mb-1">Total Dist.</div>
                     <div className="text-xl font-bold text-gray-900 dark:text-white">{totalDistance.toFixed(0)} km</div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                     <div className="text-gray-500 text-xs mb-1">Koset Dist.</div>
                     <div className="text-xl font-bold text-green-600">{user.koset_distance?.toFixed(0) || 0} km</div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                     <div className="text-gray-500 text-xs mb-1">Activity Dist.</div>
                     <div className="text-xl font-bold text-blue-600">{user.activity_distance?.toFixed(0) || 0} km</div>
                  </div>
               </div>
               
               {/* Achievements Section Placeholder */}
               <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="text-xs font-bold text-gray-400 mb-2 flex items-center gap-2">
                    <Trophy className="w-3 h-3" /> Achievements
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user.achievements && user.achievements.length > 0 ? (
                      user.achievements.map((ach, idx) => (
                        <Badge key={idx} tone="success" size="sm" icon={<Award className="w-3 h-3" />}>
                          {ach.name || ach}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400 italic">No achievements yet.</span>
                    )}
                  </div>
               </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex gap-4 pt-2">
               {user.status === "suspended" ? (
                 <Button 
                   onClick={handleUnsuspend} 
                   className="flex-1 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                   disabled={isSubmitting}
                 >
                   Unsuspend User
                 </Button>
               ) : (
                 <Button 
                   variant="warning"
                   onClick={() => setSuspendModalOpen(true)} 
                   className="flex-1 shadow-sm"
                 >
                   <AlertTriangle className="w-4 h-4 mr-2" />
                   Suspend User
                 </Button>
               )}
               <Button variant="danger" className="flex-1" onClick={() => toast.info("Delete coming soon")}>
                 Delete User
               </Button>
            </div>

          </div>
        </div>
        ) : activeTab === "history" ? (
          <div className="p-4">
            <EntityAuditHistory entityId={user._id} entityType="User" />
          </div>
        ) : (
          <div className="p-4">
            <EntityAuditHistory actorId={user._id} />
          </div>
        )}
      </Modal>

      {/* Nested Suspend Modal with higher Z-Index */}
      <Modal
        open={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        title="Suspend User"
        maxWidth="max-w-md"
        zIndex="z-[60]"
        showCloseButton={false} 
      >
        <div className="space-y-4 mt-2">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Are you sure you want to suspend <span className="font-semibold">{user.name}</span>?
          </p>
          <TextArea
            label="Reason *"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            rows={3}
            error={suspendError}
          />
          <TextInput
            label="Duration (Days)"
            type="number"
            value={suspendDuration}
            onChange={(e) => setSuspendDuration(e.target.value)}
            placeholder="Optional"
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setSuspendModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleSuspend} loading={isSubmitting}>Suspend</Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UserDetailDrawer;
